/**
 * Transcrição ao vivo (A135): a paciente fala e o texto vai aparecendo no
 * diário. Usa o Speech-to-Text standard do Google, cobrado por segundo.
 *
 * O custo é controlado por um pool mensal de minutos dividido entre todas
 * as marcas que têm a funcionalidade TRANSCRICAO ligada, mais um limite
 * diário por pessoa. Os dois são configuração do mantenedor. O mês vira no
 * dia 1º, no horário de São Paulo.
 *
 * O áudio nunca é guardado: passa pela API a caminho do Google e só o texto
 * volta, para o campo que a paciente está escrevendo.
 */
import { z } from 'zod';

export const POOL_DE_TRANSCRICAO_PADRAO_MIN = 600;
export const LIMITE_DIARIO_DE_TRANSCRICAO_PADRAO_MIN = 15;

/** O documento único de configuração (`configDaTranscricao/geral`). */
export const ConfigDaTranscricaoSchema = z.object({
  id: z.string().default('geral'),
  /** Minutos por mês, somando todas as marcas. */
  poolMinutosPorMes: z.number().int().min(0).max(100_000).default(POOL_DE_TRANSCRICAO_PADRAO_MIN),
  /** Minutos por dia que cada pessoa pode transcrever. */
  limiteDiarioMinutosPorPessoa: z.number().int().min(1).max(600).default(LIMITE_DIARIO_DE_TRANSCRICAO_PADRAO_MIN),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  updatedBy: z.string().nullish(),
});
export type ConfigDaTranscricao = z.infer<typeof ConfigDaTranscricaoSchema>;

export const AtualizarConfigDaTranscricaoSchema = ConfigDaTranscricaoSchema.pick({
  poolMinutosPorMes: true,
  limiteDiarioMinutosPorPessoa: true,
}).partial();

/**
 * Um contador de uso (`usoDeTranscricao/<id>`). Três tipos, todos somados por
 * incremento atômico, nunca lidos-e-regravados:
 * - MES: o total do pool no mês (`mes__2026-10`);
 * - MARCA: o da marca no mês, com as pessoas distintas (`marca__2026-10__<brandId>`);
 * - PESSOA: o de uma pessoa num dia (`pessoa__2026-10-10__<userId>`).
 */
export const UsoDeTranscricaoSchema = z.object({
  tipo: z.enum(['MES', 'MARCA', 'PESSOA']),
  mes: z.string().regex(/^\d{4}-\d{2}$/),
  dia: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  brandId: z.string().optional(),
  userId: z.string().optional(),
  segundos: z.number().min(0).default(0),
  /** Só no tipo MARCA: os `userId` de quem usou no mês. */
  pessoas: z.array(z.string()).optional(),
  updatedAt: z.coerce.date().optional(),
});
export type UsoDeTranscricao = z.infer<typeof UsoDeTranscricaoSchema>;

/** Por que a transcrição não está disponível agora, quando a funcionalidade está ligada. */
export type MotivoDeEsgotamento = 'MES' | 'DIA';

/** O que o app da paciente precisa saber antes de mostrar o microfone. */
export interface DisponibilidadeDaTranscricao {
  /** A marca tem a funcionalidade. Sem ela, o microfone não aparece. */
  disponivel: boolean;
  esgotou: MotivoDeEsgotamento | null;
  /** Segundos que restam agora: o menor entre o pool do mês e o limite do dia. */
  restanteSegundos: number;
  /** `AAAA-MM-DD` em que o motivo do esgotamento se renova. */
  renovaEm: string | null;
}

/** O painel do mantenedor, na chave da funcionalidade. */
export interface UsoDaTranscricaoDaMarca {
  mes: string;
  config: { poolMinutosPorMes: number; limiteDiarioMinutosPorPessoa: number };
  /** Segundos consumidos no mês por todas as marcas. */
  poolUsadoSegundos: number;
  marca: { segundos: number; pessoas: number };
  /** Quantas marcas consumiram algo neste mês. */
  marcasQueUsaram: number;
}

/** O que a API manda pelo WebSocket. */
export type MensagemDaTranscricao =
  | { tipo: 'pronto'; restanteSegundos: number }
  | { tipo: 'parcial'; texto: string }
  | { tipo: 'final'; texto: string }
  | { tipo: 'esgotou'; motivo: MotivoDeEsgotamento; renovaEm: string }
  | { tipo: 'erro'; mensagem: string }
  | { tipo: 'encerrado' };

/** O formato do áudio que o app envia: PCM 16 bits, mono, 16 kHz. */
export const TAXA_DO_AUDIO_DA_TRANSCRICAO = 16_000;
export const BYTES_POR_SEGUNDO_DA_TRANSCRICAO = TAXA_DO_AUDIO_DA_TRANSCRICAO * 2;

const FUSO = 'America/Sao_Paulo';

/** `AAAA-MM-DD` no horário de São Paulo. */
export function diaDaTranscricao(agora: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: FUSO, year: 'numeric', month: '2-digit', day: '2-digit' }).format(agora);
}

/** `AAAA-MM` no horário de São Paulo. */
export function mesDaTranscricao(agora: Date = new Date()): string {
  return diaDaTranscricao(agora).slice(0, 7);
}

/** O dia 1º do mês seguinte, `AAAA-MM-DD`. */
export function renovacaoDoPool(agora: Date = new Date()): string {
  const [ano, mes] = mesDaTranscricao(agora).split('-').map(Number);
  return mes === 12 ? `${ano + 1}-01-01` : `${ano}-${String(mes + 1).padStart(2, '0')}-01`;
}

/** O dia seguinte, `AAAA-MM-DD`. */
export function renovacaoDoDia(agora: Date = new Date()): string {
  const [a, m, d] = diaDaTranscricao(agora).split('-').map(Number);
  const amanha = new Date(Date.UTC(a, m - 1, d + 1));
  return amanha.toISOString().slice(0, 10);
}
